import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { QRCodeSVG } from 'qrcode.react';
import { checkInvoiceStatus } from '@/services/bookingService';
import showToast from '@/components/ui/Toast';

export const QrisPaymentPage: React.FC = () => {
  const { invoiceNumber: paramInvoiceNumber } = useParams<{ invoiceNumber: string }>();
  const location = useLocation();
  const state = (location.state as any) || {};

  const invoiceNumber = paramInvoiceNumber || state.invoiceNumber || state.invoice_number || '';

  const [loading, setLoading] = useState<boolean>(true);
  const [isCheckingStatus, setIsCheckingStatus] = useState<boolean>(false);
  const [arenaName, setArenaName] = useState<string>(state.arenaName || state.arena_name || 'Sporto Arena');
  const [customerName, setCustomerName] = useState<string>(state.customerName || state.customer_name || 'Pelanggan');
  const [orderDescription, setOrderDescription] = useState<string>(
    state.orderDescription || state.order_description || state.productInfo || 'Pesanan Sporto'
  );
  const [totalAmount, setTotalAmount] = useState<number>(state.totalAmount || state.grandTotal || 0);
  const [qrValue, setQrValue] = useState<string>(state.qrString || state.qrValue || '');
  const [isPaid, setIsPaid] = useState<boolean>(state.isPaid || false);
  const [isCopiedInvoice, setIsCopiedInvoice] = useState<boolean>(false);

  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleCopyInvoice = () => {
    if (!invoiceNumber) return;
    navigator.clipboard.writeText(invoiceNumber);
    setIsCopiedInvoice(true);
    showToast.success('Invoice ID berhasil disalin');
    setTimeout(() => setIsCopiedInvoice(false), 2000);
  };

  // Helper to extract QR value from various payload structures
  const extractQrFromPayload = (data: any): string => {
    if (!data) return '';
    if (data.qr_string) return data.qr_string;
    if (data.payment_url) return data.payment_url;

    const payload = data.payment_checkout_payload;
    if (payload) {
      const present = payload?.instructions?.present || payload?.actions?.present;
      if (Array.isArray(present) && present.length > 0) {
        const qrItem = present.find(
          (item: any) =>
            item?.kind === 'QR_STRING' ||
            item?.kind === 'QR_CODE' ||
            item?.kind === 'QRIS' ||
            item?.label?.toLowerCase().includes('qris') ||
            item?.label?.toLowerCase().includes('qr')
        );
        if (qrItem && (qrItem.value || qrItem.raw_value)) return qrItem.value || qrItem.raw_value;
        if (present[0]?.value) return present[0].value;
      }
      if (payload.qr_string) return payload.qr_string;
      if (payload.qris_string) return payload.qris_string;
      if (payload.qr_code) return payload.qr_code;
    }
    return '';
  };

  const fetchStatus = async (isInitial = false): Promise<'paid' | 'pending' | 'error'> => {
    if (!invoiceNumber) {
      if (isInitial) setLoading(false);
      return 'error';
    }

    try {
      const res = await checkInvoiceStatus(invoiceNumber);
      if (res.success && res.data) {
        const d = res.data;
        if (d.arena_name) setArenaName(d.arena_name);
        if (d.customer_name) setCustomerName(d.customer_name);
        if (d.order_description) setOrderDescription(d.order_description);
        if (typeof d.total_amount === 'number' && d.total_amount > 0) setTotalAmount(d.total_amount);

        const extractedQr = extractQrFromPayload(d);
        if (extractedQr) {
          setQrValue(extractedQr);
        }

        if (d.is_paid || d.status === 'PAID' || d.status === 'COMPLETED') {
          setIsPaid(true);
          return 'paid';
        }
        return 'pending';
      }
      return 'error';
    } catch (err) {
      console.error('Failed to fetch invoice status:', err);
      return 'error';
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  useEffect(() => {
    void fetchStatus(true);
  }, [invoiceNumber]);

  // Live polling for payment completion while not yet paid
  useEffect(() => {
    if (isPaid || !invoiceNumber) {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      return;
    }

    pollTimerRef.current = setInterval(() => {
      void fetchStatus(false);
    }, 2500);

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [isPaid, invoiceNumber]);

  const handleCheckPaymentStatus = async () => {
    if (!invoiceNumber || isCheckingStatus || isPaid) return;
    setIsCheckingStatus(true);
    try {
      const result = await fetchStatus(false);
      if (result === 'paid') {
        showToast.success('Pembayaran berhasil terverifikasi lunas!');
      } else if (result === 'pending') {
        showToast.info('Pembayaran belum diterima. Silakan selesaikan scan QRIS.');
      } else {
        showToast.error('Gagal memeriksa status pembayaran. Silakan coba lagi.');
      }
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const handleBack = () => {
    if (window.opener) {
      window.close();
    } else if (window.history.length > 1) {
      window.history.back();
    } else {
      window.close();
    }
  };

  const formatRupiah = (val: number): string => {
    return 'Rp. ' + (val || 0).toLocaleString('id-ID');
  };

  return (
    <div className="min-h-screen bg-white flex flex-col font-sans select-none text-[#0E1726]">
      {/* Top Header Bar */}
      <header className="w-full border-b border-gray-100 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          {/* Back button */}
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-[#FC480D] hover:text-[#D83B08] transition-colors cursor-pointer py-1.5 px-1 rounded-md"
          >
            <Icon icon="ph:arrow-left-bold" className="w-3.5 h-3.5" />
            <span>Kembali</span>
          </button>

          {/* Sporto Branding */}
          <img
            src="https://cdn.sporto.id/sporto-black.png"
            alt="Sporto"
            className="h-7 sm:h-8 w-auto object-contain select-none"
          />
        </div>
      </header>

      {/* Main Payment Section */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-8 sm:py-12">
        <div className="w-full max-w-md mx-auto flex flex-col items-center text-center space-y-6">
          {/* Arena Name & Title */}
          <div className="space-y-1">
            <p className="text-xs text-gray-400 font-semibold tracking-wide uppercase">
              {arenaName}
            </p>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0E1726] tracking-tight">
              Scan untuk bayar
            </h1>
            {invoiceNumber && (
              <div className="pt-0.5">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-gray-100/80 text-gray-600 font-mono text-[11px] font-semibold border border-gray-200">
                  <span className="text-gray-400">Invoice:</span>
                  <span className="font-bold text-[#0E1726]">{invoiceNumber}</span>
                </span>
              </div>
            )}
          </div>

          {/* QR Card Container */}
          <div className="w-64 sm:w-72 bg-white rounded-3xl p-6 sm:p-7 border border-gray-100 shadow-[0_4px_25px_-5px_rgba(0,0,0,0.06)] flex flex-col items-center justify-center relative transition-transform hover:scale-[1.01]">
            {loading ? (
              <div className="w-48 h-48 flex flex-col items-center justify-center gap-2 text-gray-400">
                <div className="w-8 h-8 border-3 border-[#FC480D] border-t-transparent rounded-full animate-spin" />
                <span className="text-[11px] font-medium">Memuat QRIS...</span>
              </div>
            ) : isPaid ? (
              <div className="w-48 h-48 flex flex-col items-center justify-center gap-3">
                <div className="w-16 h-16 rounded-full bg-emerald-50 border-2 border-emerald-100 flex items-center justify-center animate-bounce">
                  <Icon icon="ph:check-circle-fill" className="w-10 h-10 text-emerald-500" />
                </div>
                <div className="space-y-0.5">
                  <p className="text-xs font-bold text-emerald-600">Terverifikasi Lunas</p>
                  <p className="text-[10px] text-gray-400 font-mono">{invoiceNumber}</p>
                </div>
              </div>
            ) : qrValue ? (
              <>
                <div className="p-1 bg-white rounded-xl">
                  <QRCodeSVG
                    value={qrValue}
                    size={190}
                    level="M"
                    includeMargin={false}
                  />
                </div>
                <div className="mt-4 text-xs font-black tracking-widest text-[#0E1726]">
                  QRIS
                </div>
              </>
            ) : (
              <div className="w-48 h-48 flex flex-col items-center justify-center gap-3 text-center px-2">
                <Icon icon="ph:qr-code-duotone" className="w-10 h-10 text-gray-300" />
                <p className="text-xs font-medium text-gray-500">
                  Kode QRIS belum tersedia
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setLoading(true);
                    void fetchStatus(true);
                  }}
                  className="px-3.5 py-1.5 rounded-full bg-[#FFF5EE] text-[#FC480D] hover:bg-orange-100 text-xs font-semibold transition-colors cursor-pointer"
                >
                  Muat ulang QRIS
                </button>
              </div>
            )}
          </div>

          {/* Total Amount Display */}
          <div className="space-y-0.5">
            <p className="text-xs text-gray-400 font-medium">Total pembayaran</p>
            <p className="text-2xl sm:text-3xl font-black text-[#FC480D] tracking-tight">
              {formatRupiah(totalAmount)}
            </p>
          </div>

          {/* Order Details Card */}
          <div className="w-full max-w-sm bg-gray-50/80 rounded-2xl p-4 border border-gray-100 text-xs text-left space-y-2.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-gray-400 font-medium shrink-0">Invoice ID</span>
              <div className="flex items-center gap-1.5 font-mono font-bold text-[#0E1726]">
                <span className="select-all">{invoiceNumber || '-'}</span>
                {invoiceNumber && (
                  <button
                    type="button"
                    onClick={handleCopyInvoice}
                    className="p-1 rounded-md text-gray-400 hover:text-[#FC480D] hover:bg-orange-50 transition-colors cursor-pointer"
                    title="Salin Invoice ID"
                  >
                    <Icon
                      icon={isCopiedInvoice ? 'ph:check-bold' : 'ph:copy-bold'}
                      className={`w-3.5 h-3.5 ${isCopiedInvoice ? 'text-emerald-500' : 'text-[#FC480D]'}`}
                    />
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-start justify-between gap-3 border-t border-gray-100/70 pt-2">
              <span className="text-gray-400 font-medium shrink-0">Atas nama</span>
              <span className="font-bold text-[#0E1726] text-right truncate">
                {customerName}
              </span>
            </div>
            <div className="flex items-start justify-between gap-3 border-t border-gray-100/70 pt-2">
              <span className="text-gray-400 font-medium shrink-0">Pesanan</span>
              <span className="font-bold text-[#0E1726] text-right line-clamp-2">
                {orderDescription}
              </span>
            </div>
          </div>

          {/* Payment Status Indicator */}
          <div className="pt-1">
            {isPaid ? (
              <div className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 px-4 py-2 rounded-full text-xs font-extrabold shadow-2xs">
                <Icon icon="ph:check-circle-fill" className="w-4 h-4 text-emerald-500" />
                <span>Pembayaran Berhasil! Terverifikasi lunas.</span>
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 text-xs font-bold text-amber-500 animate-pulse">
                <Icon icon="ph:sparkle-fill" className="w-3.5 h-3.5 text-amber-500" />
                <span>Menunggu pembayaran..</span>
              </div>
            )}
          </div>

          {/* Check Payment Status Button */}
          {!isPaid && (
            <div className="pt-2">
              <button
                type="button"
                disabled={isCheckingStatus || !invoiceNumber}
                onClick={handleCheckPaymentStatus}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border border-gray-200 hover:border-[#FC480D]/40 hover:bg-[#FFF5EE] text-xs font-semibold text-[#0E1726] hover:text-[#FC480D] transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed bg-white shadow-2xs"
              >
                <Icon
                  icon="ph:arrows-clockwise-bold"
                  className={`w-3.5 h-3.5 text-[#FC480D] ${isCheckingStatus ? 'animate-spin' : ''}`}
                />
                <span>{isCheckingStatus ? 'Mengecek status...' : 'Cek Status Pembayaran'}</span>
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
